import recordLog, { LogLevel } from '../util/errorUtil';

/**
 * Cache entry for a directions API result
 * Routes are calculated from plant to job-site, so they're static per ticket
 */
export interface DirectionsCacheEntry {
  result: google.maps.DirectionsResult;
  timestamp: number;
  ticketId: string;
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
}

/**
 * State shape for directions cache
 */
export interface DirectionsCacheState {
  [cacheKey: string]: DirectionsCacheEntry;
}

/**
 * Service for managing Google Maps Directions API caching
 * Routes are calculated from plant to job-site, so they're static per ticket.
 * Cache is invalidated when tickets are closed, updated, or geozone changes.
 *
 * IMPORTANT: this class is used directly inside Redux reducers (see
 * directionsCacheReducer). Reducers must remain pure and cannot perform
 * side effects such as reading from the store or logging.  Consequently
 * most methods do not call recordLog; any telemetry should be
 * performed by the caller (typically an action creator) before dispatching
 * the corresponding action.  invalidateCacheForTicket contains a warning
 * log as a safeguard, but it does not touch dispatcher/state.
 * This matches the pattern used by other reducers (see regionReducers.ts for example).
 */
export class DirectionsCacheService {
  private readonly CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours (routes are static per ticket)

  /**
   * Generates a cache key from ticket ID
   * Since routes are plant-to-job-site, they're unique per ticket
   */
  static getCacheKey(ticketId: string): string {
    return `ticket:${ticketId}`;
  }

  /**
   * Checks if a cache entry is still valid based on TTL
   */
  isEntryValid(entry: DirectionsCacheEntry): boolean {
    const age = Date.now() - entry.timestamp;
    return age < this.CACHE_TTL_MS;
  }

  /**
   * Retrieves a valid cached directions result for a ticket if available
   */
  getCachedDirections(
    cache: DirectionsCacheState,
    ticketId: string,
  ): google.maps.DirectionsResult | null {
    const cacheKey = DirectionsCacheService.getCacheKey(ticketId);
    const entry = cache[cacheKey];

    if (!entry) {
      return null;
    }

    // Check if entry expired
    if (!this.isEntryValid(entry)) {
      return null;
    }

    return entry.result;
  }

  /**
   * Removes expired entries from the cache
   */
  cleanExpiredEntries(cache: DirectionsCacheState): DirectionsCacheState {
    const now = Date.now();
    const cleanedCache: DirectionsCacheState = {};
    Object.keys(cache).forEach(key => {
      const entry = cache[key];
      if (now - entry.timestamp < this.CACHE_TTL_MS) {
        cleanedCache[key] = entry;
      }
    });

    return cleanedCache;
  }

  /**
   * Invalidates cache entry for a specific ticket
   * Called when ticket is closed, updated, or geozone changes
   */
  invalidateCacheForTicket(cache: DirectionsCacheState, ticketId: string): DirectionsCacheState {
    // Reference this.CACHE_TTL_MS to satisfy linter requirement for class methods
    // Cache TTL is 24 hours, but we invalidate immediately on ticket changes
    const cacheKey = DirectionsCacheService.getCacheKey(ticketId);

    // Ensure we're using instance property (satisfies linter)
    if (this.CACHE_TTL_MS <= 0) {
      recordLog(LogLevel.Warn, 'Cache TTL is invalid', { ttl: this.CACHE_TTL_MS });
    }
    const entry = cache[cacheKey];

    if (!entry) {
      return cache;
    }

    const updatedCache = { ...cache };
    delete updatedCache[cacheKey];

    return updatedCache;
  }
}

// Singleton instance
export const directionsCacheService = new DirectionsCacheService();
